import { type DragEvent, type JSX, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@ttab/elephant-ui'
import { PlusIcon, XIcon } from '@ttab/elephant-ui/icons'
import { cn } from '@ttab/elephant-ui/utils'
import type { Block } from '@ttab/elephant-api/newsdoc'
import type { MemoBlock, MemoTemplate } from '@/shared/templates/memos'
import type { MemoItem, MemoLine } from '@/shared/templates/memos/lib/renderMemo'
import type { InsertedMemo } from '@/shared/templates/memos/lib/insertedDocument'
import { ValidationCue } from '@/components/ValidationCue'
import { ValidationIcon } from '@/components/ValidationIcon'
import { holdsDocument } from '../lib/blockCopies'
import { canDropNear, hasFixedPlace } from '../lib/moveSelected'
import { InsertableDocuments } from './InsertableDocuments'
import type { Insertable } from './InsertableDocuments'
import { PreviewDocument } from './PreviewDocument'
import { PreviewHeading } from './PreviewHeading'
import { PreviewItem } from './PreviewItem'
import { PreviewLine } from './PreviewLine'

type PreviewGroup
  = | { type: 'text', key: string, line: MemoLine }
    | { type: 'item', key: string, itemId: string, lines: MemoLine[] }
    | { type: 'block-header', key: string, blockId: string, label: string }
    | { type: 'document', key: string, documentId: string, lines: MemoLine[] }

/**
 * Where a dragged item would land if dropped right now - immediately before
 * or after another item, or into a block that has no item of its own to
 * anchor the drop to (an empty block, or a column it has nothing in yet).
 */
type DropTarget
  = | { kind: 'item', neighborId: string, position: 'before' | 'after' }
    | { kind: 'block', blockId: string }

const dropMarker = (key: string) => <div key={key} className='h-0.5 shrink-0 rounded-full bg-primary' />

/** The space that sets a block's events apart from its material above them. */
const eventsGap = (key: string) => (
  <div key={key} role='separator' className='h-4 shrink-0' />
)

/**
 * The memo as it will be created, and the controls that reach into it: the
 * drag handles and keyword pickers of the items, the crosses of the inserted
 * PMs, and the blocks the editor can take out and put back.
 */
export const MemoPreview = ({
  lines,
  template,
  activeBlocks,
  removedBlocks,
  selected,
  unassignedCount,
  insertedById,
  insertable,
  onDropNear,
  onDropIntoBlock,
  onRemoveItem,
  onSetKeyword,
  onRemoveBlock,
  onInsertBlock,
  onRemoveInserted,
  onRemoveInsertedLine,
  onAssignUnassigned,
  editableHeading,
  invalid,
  itemsRequired
}: {
  /** The memo's rendered lines, flat, as `renderMemoLines` returns them. */
  lines: MemoLine[]
  template: MemoTemplate | undefined
  activeBlocks: MemoBlock[]
  removedBlocks: MemoBlock[]
  selected: MemoItem[]
  /** Items picked but not yet given a block - not in `selected`, so not in `lines` either. */
  unassignedCount: number
  insertedById: Map<string, InsertedMemo>
  /** The day's published PMs, for a template that offers any. */
  insertable?: Insertable
  onDropNear: (item: MemoItem, neighborId: string, position: 'before' | 'after') => void
  onDropIntoBlock: (item: MemoItem, blockId: string) => void
  onRemoveItem: (item: MemoItem) => void
  onSetKeyword: (item: MemoItem, keyword: string | undefined) => void
  onRemoveBlock: (blockId: string) => void
  onInsertBlock: (blockId: string) => void
  onRemoveInserted: (documentId: string) => void
  onRemoveInsertedLine: (documentId: string, block: Block | undefined) => void
  onAssignUnassigned: (blockId: string) => void
  /**
   * The memo's heading, which is written here rather than among the fields
   * above - the preview being where a headline is read. Read-only while the
   * dialog creates a PM per type, each headed with its own.
   */
  editableHeading: { value: string, onChange: (value: string) => void, readOnly?: boolean }
  /**
   * What in the preview to mark as missing: the heading, items still waiting
   * on a block, or items at all - the last on the `itemsRequired` hint.
   */
  invalid?: { heading?: boolean, unassigned?: boolean, items?: boolean }
  /** Shows the ask for posts: the template needs items and none are picked yet. */
  itemsRequired?: boolean
}): JSX.Element => {
  const { t } = useTranslation()

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)

  const itemsById = useMemo(
    () => new Map(selected.map((item) => [item.id, item])),
    [selected]
  )

  const draggedItem = draggedItemId ? itemsById.get(draggedItemId) : undefined
  const blocks = template?.blocks ?? []

  const endDrag = () => {
    setDraggedItemId(null)
    setDropTarget(null)
  }

  // Shows the marker/accepts the drop once the hovered item is one the
  // dragged item could actually land next to without rewriting a fact about
  // it (see `canDropNear`). Hovering an item that isn't a valid neighbor -
  // wrong column, or a day/section mismatch - but does belong to a
  // different block still offers that block as a target, the same one its
  // header row offers, so reaching into a block that already has content
  // doesn't mean hunting for the one header row that accepts the drop.
  const handleItemDragOver = (item: MemoItem) => (event: DragEvent<HTMLDivElement>) => {
    if (!draggedItem || draggedItem.id === item.id) {
      return
    }

    if (canDropNear(draggedItem, item, blocks, selected)) {
      const rect = event.currentTarget.getBoundingClientRect()
      const position = event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'

      event.preventDefault()
      setDropTarget((current) => (
        current?.kind === 'item' && current.neighborId === item.id && current.position === position
          ? current
          : { kind: 'item', neighborId: item.id, position }
      ))
      return
    }

    if (item.blockId !== draggedItem.blockId && !holdsDocument(selected, item.blockId, draggedItem)) {
      event.preventDefault()
      setDropTarget((current) => (
        current?.kind === 'block' && current.blockId === item.blockId
          ? current
          : { kind: 'block', blockId: item.blockId }
      ))
    }
  }

  const handleItemDrop = (item: MemoItem) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (draggedItem && dropTarget?.kind === 'item' && dropTarget.neighborId === item.id) {
      onDropNear(draggedItem, dropTarget.neighborId, dropTarget.position)
    } else if (draggedItem && dropTarget?.kind === 'block' && dropTarget.blockId === item.blockId) {
      onDropIntoBlock(draggedItem, dropTarget.blockId)
    }

    endDrag()
  }

  // A block with nothing of its own for the item to land next to - empty, or
  // with nothing yet in the dragged item's column - still needs somewhere to
  // drop onto, so its header row doubles as a target. Not a block that already
  // holds the dragged item's document, though: the drop would be refused.
  const handleBlockDragOver = (blockId: string) => (event: DragEvent<HTMLDivElement>) => {
    if (!draggedItem || holdsDocument(selected, blockId, draggedItem)) {
      return
    }

    event.preventDefault()
    setDropTarget((current) => (
      current?.kind === 'block' && current.blockId === blockId ? current : { kind: 'block', blockId }
    ))
  }

  const handleBlockDrop = (blockId: string) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (draggedItem) {
      onDropIntoBlock(draggedItem, blockId)
    }

    endDrag()
  }

  // Removing/reinserting a block only makes sense once there is more than one
  // to arrange - a single-block template has nothing for the controls to do.
  const blocksManaged = (template?.blocks.length ?? 0) > 1

  // The memo needs a block to put its items in, so the last active one keeps
  // no remove control - its header is still shown, and the removed ones can
  // still be added back from the strip below the preview.
  const blocksRemovable = activeBlocks.length > 1

  // Only a memo with a block to choose between leaves a pick waiting - see
  // `additionsFor`. Kept up while anything still waits, whatever the blocks.
  const hasUnassigned = unassignedCount > 0
  const showsBatchBar = hasUnassigned
    || (activeBlocks.length > 1 && !template?.insertsIntoEveryBlock)

  // A PM pinned to a block is offered in that block's header, where the block's
  // label says where it goes. Everything else - no pin, a removed pinned block,
  // or no headers at all - is offered in the strip above the preview.
  const pinnedTo = insertable?.target.kind === 'block' && !insertable.target.restores
    ? insertable.target.blockId
    : undefined
  const showsInHeader = !!pinnedTo && blocksManaged

  // Regroup the flat rendered lines back into per-item blocks, so each item
  // can carry its own drag handle while headings/preamble - lines no item
  // owns - stay plain text. `renderItems` always emits one item's
  // lines together, so collapsing consecutive same-`itemId` lines is enough,
  // and the same holds for the run of lines an inserted PM contributes.
  //
  // A `block-header` group is injected for every active block, in template
  // order, whether or not it rendered any lines of its own - an empty block
  // gets nothing but its header, but it still needs somewhere to show the
  // remove control and give the editor a target to move items into. Headers
  // for any active blocks a rendered line's own block was preceded by (blocks
  // with no lines of their own) are flushed right before it; any left over
  // once the loop ends (trailing empty blocks) are flushed after.
  const previewGroups = useMemo((): PreviewGroup[] => {
    const groups: PreviewGroup[] = []
    let lastActiveIndex = -1

    const flushHeadersUpTo = (activeIndex: number) => {
      for (let index = lastActiveIndex + 1; index <= activeIndex; index++) {
        const block = activeBlocks[index]

        groups.push({ type: 'block-header', key: `block-${block.id}`, blockId: block.id, label: block.label })
      }

      lastActiveIndex = Math.max(lastActiveIndex, activeIndex)
    }

    for (const [index, line] of lines.entries()) {
      if (line.blockId) {
        const activeIndex = activeBlocks.findIndex((block) => block.id === line.blockId)

        if (activeIndex >= 0 && activeIndex > lastActiveIndex) {
          flushHeadersUpTo(activeIndex)
        }
      }

      // A block whose heading repeats its label would read it twice in the
      // preview: once in the header row carrying the remove control, once as
      // the heading itself. Only the header row is preview chrome, so the
      // heading is the one to drop here - the created memo still gets it.
      //
      // Not in a memo grouped by day, though: there the block recurs under each
      // day while the header row is emitted once, so dropping the heading would
      // leave every day but the first with its items run together.
      if (line.blockHeading && blocksManaged && !template?.dayGrouping) {
        const block = activeBlocks.find((entry) => entry.id === line.blockId)

        if (block && block.heading === block.label) {
          continue
        }
      }

      const last = groups.at(-1)

      if (line.documentId) {
        if (last?.type === 'document' && last.documentId === line.documentId) {
          last.lines.push(line)
          continue
        }

        groups.push({
          type: 'document',
          key: `${index}-${line.documentId}`,
          documentId: line.documentId,
          lines: [line]
        })
        continue
      }

      if (line.itemId && last?.type === 'item' && last.itemId === line.itemId) {
        last.lines.push(line)
        continue
      }

      if (line.itemId) {
        groups.push({ type: 'item', key: `${index}-${line.itemId}`, itemId: line.itemId, lines: [line] })
        continue
      }

      // The title is keyed on being the title rather than on what it says: it
      // is the one line that can be a field, and a key that moved with the text
      // would hand that field a new identity per keystroke - remounting it, and
      // taking the cursor out of it, on every letter typed.
      groups.push({
        type: 'text',
        key: line.role === 'heading-1' ? 'heading-1' : `${index}-${line.text}`,
        line
      })
    }

    flushHeadersUpTo(activeBlocks.length - 1)

    return groups
  }, [lines, activeBlocks, blocksManaged, template?.dayGrouping])

  return (
    <section aria-label={t('memo:labels.preview')} className='flex flex-col gap-2 min-w-0'>
      {/* Items still waiting on a block are marked on the preview as a whole. */}
      <ValidationCue show={!!invalid?.unassigned} className='w-fit pr-5'>
        <span className='text-sm font-medium'>{t('memo:labels.preview')}</span>
      </ValidationCue>

      {!!insertable && !showsInHeader && <InsertableDocuments insertable={insertable} />}

      {/*
        Items picked but not yet given a block sit in their candidate column,
        not here - this bar is the only place a batch of them is resolved into
        one block at once, all together rather than one at a time. Shown grayed
        out while nothing waits, so the first pick does not shift the layout.
      */}
      {showsBatchBar && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <span className={cn(!hasUnassigned && 'opacity-50')}>
            {t('memo:labels.unassignedCount', { count: unassignedCount })}
          </span>

          <DropdownMenu>
            <DropdownMenuTrigger render={(
              <Button
                variant={hasUnassigned ? 'default' : 'outline'}
                size='xs'
                disabled={!hasUnassigned}
              >
                {t('memo:labels.insertSelectedInto')}
              </Button>
            )}
            />

            <DropdownMenuContent align='start'>
              <DropdownMenuLabel>{t('memo:labels.chooseBlock')}</DropdownMenuLabel>
              {activeBlocks.map((block) => (
                <DropdownMenuItem key={block.id} onClick={() => onAssignUnassigned(block.id)}>
                  {block.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/*
        Every line is shown as what the created document will carry: the title
        as the one heading a memo of ours has, and a block or section heading as
        the plain text it is saved as, telling its part apart by standing on a
        line of its own - see `PreviewLine`.
      */}
      <div className='text-sm bg-muted rounded-md p-3 w-full overflow-y-auto max-h-[30rem] @4xl:flex-1 @4xl:min-h-72 @4xl:max-h-none'>
        {previewGroups.flatMap((group, groupIndex) => {
          if (group.type === 'block-header') {
            if (!blocksManaged) {
              return []
            }

            return [
              <div
                key={group.key}
                role='group'
                aria-label={group.label}
                className='flex flex-col gap-1 pt-1'
                onDragOver={handleBlockDragOver(group.blockId)}
                onDrop={handleBlockDrop(group.blockId)}
              >
                <div className='flex items-center justify-between gap-2'>
                  <span className='text-xs font-medium text-muted-foreground'>
                    {group.label}
                  </span>

                  {blocksRemovable && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='size-6 shrink-0 p-0 text-muted-foreground'
                      aria-label={t('memo:actions.removeBlock')}
                      onClick={() => onRemoveBlock(group.blockId)}
                    >
                      <XIcon size={14} strokeWidth={1.75} />
                    </Button>
                  )}
                </div>

                {/* On a row of its own below the label, so it never crowds it. */}
                {!!insertable && showsInHeader && group.blockId === pinnedTo && (
                  <InsertableDocuments insertable={insertable} inline />
                )}
              </div>,
              ...(dropTarget?.kind === 'block' && dropTarget.blockId === group.blockId
                ? [dropMarker(`${group.key}-marker`)]
                : [])
            ]
          }

          if (group.type === 'text') {
            if (group.line.role === 'heading-1') {
              return [
                <ValidationCue key={group.key} show={!!invalid?.heading}>
                  <PreviewHeading
                    label={t('memo:labels.memoTitle')}
                    value={editableHeading.value}
                    onValueChange={editableHeading.onChange}
                    readOnly={editableHeading.readOnly}
                  />
                </ValidationCue>
              ]
            }

            return [
              ...(group.line.eventsStart ? [eventsGap(`${group.key}-events`)] : []),
              <PreviewLine key={group.key} line={group.line} />
            ]
          }

          if (group.type === 'document') {
            return [
              <PreviewDocument
                key={group.key}
                title={insertedById.get(group.documentId)?.title}
                lines={group.lines}
                onRemove={() => onRemoveInserted(group.documentId)}
                onRemoveLine={(block) => onRemoveInsertedLine(group.documentId, block)}
              />
            ]
          }

          const item = itemsById.get(group.itemId)

          if (!item) {
            return group.lines.map((line, index) => (
              <p key={`${group.key}-${index}`} className='whitespace-pre-wrap'>{line.text}</p>
            ))
          }

          const nodes: JSX.Element[] = []
          const startsEvents = !!group.lines[0]?.eventsStart

          if (startsEvents) {
            nodes.push(eventsGap(`${group.key}-events`))
          }

          if (dropTarget?.kind === 'item' && dropTarget.neighborId === item.id && dropTarget.position === 'before') {
            nodes.push(dropMarker(`${group.key}-marker-before`))
          }

          nodes.push(
            <PreviewItem
              key={group.key}
              item={item}
              lines={group.lines}
              keywords={template?.blocks.find((block) => block.id === item.blockId)?.keywords ?? []}
              // A hairline between neighboring items, so an item running over
              // several lines does not read as part of the one above it. Only
              // between items: a heading or a block header already separates
              // what follows it, and a rule after the last item would look
              // like the memo continues. Nor above the events, which the gap
              // already sets apart.
              separated={!startsEvents && previewGroups[groupIndex - 1]?.type === 'item'}
              fixed={hasFixedPlace(item, blocks)}
              onlyItem={!!template?.singleItem}
              onDragStart={() => setDraggedItemId(item.id)}
              onDragEnd={endDrag}
              onDragOver={handleItemDragOver(item)}
              onDrop={handleItemDrop(item)}
              onRemove={() => onRemoveItem(item)}
              onKeywordChange={(keyword) => onSetKeyword(item, keyword)}
            />
          )

          if (dropTarget?.kind === 'item' && dropTarget.neighborId === item.id && dropTarget.position === 'after') {
            nodes.push(dropMarker(`${group.key}-marker-after`))
          }

          return nodes
        })}

        {/* Shown from the start, gray; the triangle joins once a create is refused. */}
        {itemsRequired && (
          <p className='flex items-center gap-1.5 pt-2 text-muted-foreground'>
            {invalid?.items && <ValidationIcon className='flex shrink-0' />}
            {t('memo:errors.itemsRequired')}
          </p>
        )}
      </div>

      {removedBlocks.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {removedBlocks.map((block) => (
            <Button
              key={block.id}
              variant='outline'
              size='xs'
              className='gap-1 text-muted-foreground'
              onClick={() => onInsertBlock(block.id)}
            >
              <PlusIcon size={14} strokeWidth={1.75} />
              {t('memo:actions.addBlock', { label: block.label })}
            </Button>
          ))}
        </div>
      )}
    </section>
  )
}
